"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTheme = exports.defaultFontSize = exports.fontSizeOptions = exports.defaultColorScheme = exports.colorSchemeOptions = void 0;
const material_1 = require("@mui/material");
const colors_1 = require("@mui/material/colors");
const locale_1 = require("@mui/material/locale");
const react_1 = require("react");
exports.colorSchemeOptions = ["default", "dark", "light"];
exports.defaultColorScheme = "default";
exports.fontSizeOptions = [
    { id: "small", fontSize: 14 },
    { id: "medium", fontSize: 16 },
    { id: "large", fontSize: 20 },
];
exports.defaultFontSize = "medium";
function useTheme(args) {
    var _a, _b, _c;
    const darkMode = useDarkMode((_a = args.colorScheme) !== null && _a !== void 0 ? _a : exports.defaultColorScheme);
    const fontSize = useFontSize((_b = args.fontSize) !== null && _b !== void 0 ? _b : exports.defaultFontSize);
    const localization = useLocalization((_c = args.language) !== null && _c !== void 0 ? _c : "en");
    return (0, react_1.useMemo)(() => {
        var _a;
        const theme = (0, material_1.createTheme)({
            typography: {
                fontSize,
            },
            palette: {
                mode: darkMode ? "dark" : "light",
                primary: darkMode ? colors_1.lightBlue : colors_1.indigo,
                secondary: { main: "#a22717" },
                background: {
                    default: darkMode ? "#303030" : "#fafafa",
                    paper: darkMode ? "#424242" : "#ffffff",
                },
                grey: {
                    main: colors_1.grey[300],
                    dark: colors_1.grey[400],
                },
            },
        }, ...((_a = args.args) !== null && _a !== void 0 ? _a : []));
        return (0, material_1.responsiveFontSizes)((0, material_1.createTheme)(theme, createOverrides(theme), localization));
    }, [darkMode, fontSize, localization]);
}
exports.useTheme = useTheme;
function createOverrides(theme) {
    return {
        components: {
            MuiButton: {
                defaultProps: {
                    variant: "outlined",
                    color: "grey",
                },
                variants: [
                    {
                        props: { variant: "contained", color: "grey" },
                        style: {
                            color: theme.palette.getContrastText(theme.palette.grey[300]),
                        },
                    },
                    {
                        props: { variant: "outlined", color: "grey" },
                        style: {
                            color: theme.palette.text.primary,
                            borderColor: theme.palette.mode === "light"
                                ? "rgba(0, 0, 0, 0.23)"
                                : "rgba(255, 255, 255, 0.23)",
                            "&.Mui-disabled": {
                                border: `1px solid ${theme.palette.action.disabledBackground}`,
                            },
                            "&:hover": {
                                borderColor: theme.palette.mode === "light"
                                    ? "rgba(0, 0, 0, 0.23)"
                                    : "rgba(255, 255, 255, 0.23)",
                                backgroundColor: (0, material_1.alpha)(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                            },
                        },
                    },
                    {
                        props: { color: "grey", variant: "text" },
                        style: {
                            color: theme.palette.text.primary,
                            "&:hover": {
                                backgroundColor: (0, material_1.alpha)(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                            },
                        },
                    },
                ],
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: "unset",
                    },
                },
            },
        },
    };
}
function useFontSize(fontSize) {
    return (0, react_1.useMemo)(() => exports.fontSizeOptions.find((f) => f.id === fontSize).fontSize, [fontSize]);
}
function useDarkMode(colorScheme) {
    const prefersDarkMode = (0, material_1.useMediaQuery)("(prefers-color-scheme: dark)");
    return (0, react_1.useMemo)(() => colorScheme === "default" ? prefersDarkMode : colorScheme === "dark", [colorScheme, prefersDarkMode]);
}
function useLocalization(language) {
    switch (language) {
        case "de":
            return locale_1.deDE;
        default:
            return locale_1.enUS;
    }
}
